/**
 * POM-6010 — the single place a Review Lot & NDC row's status is derived.
 *
 * The backend query is deliberately a data provider: it returns per-row state plus a deduped
 * NDC/lot availability catalogue and classifies nothing. That keeps the hiding/blocking policy
 * changeable without a deploy, but it also means the expiry / stock / lock rules live here — and
 * only here. Scattering them across components is how the four divergent NDC chains POM-5925 had
 * to unify came about, so every rule below stays in this pure, unit-tested module and the grid
 * renders what it is told.
 *
 * No React, no network, no dates read from the clock — `todayStr` is passed in so every branch is
 * testable.
 */

import SYSTEM_DEFICIT from 'common/constants/inventoryLabels';
import { hasManualSelection } from './bulkLotNdcStaging';

export const ROW_STATUS = {
  /** Stock has already moved; changing the fill now would contradict the ledger. */
  ALREADY_DISPENSED: 'ALREADY_DISPENSED',
  /** The product is frozen for a physical count at this facility. */
  LOCKED_FOR_COUNT: 'LOCKED_FOR_COUNT',
  /** The recorded lot cannot be dispensed, but something usable exists to switch to. */
  UNUSABLE_LOT: 'UNUSABLE_LOT',
  /** The recorded lot cannot be dispensed and nothing usable exists anywhere. */
  NO_USABLE_LOT: 'NO_USABLE_LOT',
  /** No lot is recorded at all. */
  NO_LOT: 'NO_LOT',
  /** No NDC is assigned, but the product has a catalogue to pick one from. */
  NO_NDC: 'NO_NDC',
  /** The product has no NDC/lot catalogue at this facility. */
  NO_CATALOG: 'NO_CATALOG',
  /** Exactly one NDC, exactly one usable lot, already recorded: nothing to decide. */
  NO_ALTERNATIVE: 'NO_ALTERNATIVE',
  /** Recorded lot is fine and alternatives exist: listed for information, editable. */
  AS_ALLOCATED: 'AS_ALLOCATED',
  /** The user has edited this row and not yet committed. */
  CHANGED: 'CHANGED',
  /**
   * POM-6119 — the shortfall was agreed and committed. The row prints.
   *
   * Its own status rather than folding into AS_ALLOCATED: the lot still holds less than the fill,
   * so a reviewer scanning the grid should see that a claim is coming, not a row that reads as
   * ordinarily stocked.
   */
  SHORTFALL_COMMITTED: 'SHORTFALL_COMMITTED',
  /**
   * POM-6119 — the lot was typed by hand and committed. The row prints.
   *
   * Told apart from SHORTFALL_COMMITTED because the claim is the WHOLE quantity, not a remainder:
   * such a lot is created at remaining_qty 0 by design, so "holds 0 of 25" would read as a
   * problem rather than as the thing that was deliberately recorded.
   */
  MANUAL_LOT_COMMITTED: 'MANUAL_LOT_COMMITTED',
};

export const UNUSABLE_REASON = {
  EXPIRED: 'EXPIRED',
  OUT_OF_STOCK: 'OUT_OF_STOCK',
  NOT_FOUND: 'NOT_FOUND',
  // POM-6119 — in date and on the shelf, just not enough of it. Split out of OUT_OF_STOCK because
  // the remedy is the opposite: a lot holding 10 of 25 is dispensed and the rest claimed, where an
  // empty lot has nothing to dispense at all.
  SHORT_FOR_FILL: 'SHORT_FOR_FILL',
};

/**
 * Quantities for display: '4995.0000' -> '4995', '2.5000' -> '2.5'.
 *
 * Lot and fill quantities are `numeric(12,4)`, which node-postgres hands back as a string with its
 * scale intact — rendering it raw is what put "4995.0000" in the lot picker. Number's own
 * formatting drops the trailing zeros, and the toFixed pass first absorbs float-addition noise
 * from summing lots (2.9000000000000004 -> '2.9').
 *
 * Deliberately not rounded: rounding up would overstate available stock, and flooring would show
 * a lot that genuinely covers a fractional fill as '0'. Stock is whole units in practice, so this
 * reads as a whole number everywhere it matters without lying when it isn't.
 */
export const formatQty = (value) => {
  // Guarded before the coercion: Number(null) and Number('') are both 0, and rendering an unknown
  // quantity as a confident '0' is worse than rendering nothing.
  if (value == null || value === '') return '';

  const asNumber = Number(value);
  if (!Number.isFinite(asNumber)) return String(value);

  return String(Number(asNumber.toFixed(4)));
};

/** Statuses hidden from the grid and counted with the nothing-to-change majority. */
const HIDDEN_STATUSES = new Set([ROW_STATUS.NO_ALTERNATIVE]);

/**
 * Rows whose decision is already recorded.
 *
 * They keep their pencil, because the lot can still be changed, but nobody is waiting on them: the
 * commit happened and the fill will act on it. Counting them as "needs a decision" sent the
 * operator looking for work that is done — a batch whose only listed row was a committed claim
 * read "1 needs a decision" above a row that was simply printing.
 */
const SETTLED_STATUSES = new Set([ROW_STATUS.SHORTFALL_COMMITTED, ROW_STATUS.MANUAL_LOT_COMMITTED]);

/**
 * Most urgent first, blocking statuses ahead of everything else. A read-only row the user cannot
 * act on sorts last however alarming its badge looks.
 */
const SORT_ORDER = [
  ROW_STATUS.UNUSABLE_LOT,
  ROW_STATUS.NO_NDC,
  ROW_STATUS.NO_USABLE_LOT,
  ROW_STATUS.NO_CATALOG,
  ROW_STATUS.NO_LOT,
  ROW_STATUS.CHANGED,
  ROW_STATUS.SHORTFALL_COMMITTED,
  ROW_STATUS.MANUAL_LOT_COMMITTED,
  ROW_STATUS.AS_ALLOCATED,
  ROW_STATUS.ALREADY_DISPENSED,
  ROW_STATUS.LOCKED_FOR_COUNT,
];

export const statusSortRank = (status) => {
  const rank = SORT_ORDER.indexOf(status);

  return rank === -1 ? SORT_ORDER.length : rank;
};

const toDateOnly = (value) => {
  if (!value) return null;
  // Both ISO strings and Date instances arrive here depending on the GraphQL scalar.
  const asString = value instanceof Date ? value.toISOString() : String(value);

  return asString.slice(0, 10);
};

/**
 * A lot is usable when it exists, is still in date, and holds at least the full fill quantity.
 *
 * The quantity half is what makes v1 single-lot: a lot that could only partly cover the fill is not
 * offered, because there is no split editor to combine it with another.
 */
export const isLotUsable = ({ lot, fillQuantity, todayStr }) => {
  if (!lot) return false;
  if (lot.existsInInventory === false) return false;

  const expiresOn = toDateOnly(lot.expirationDate);
  if (!expiresOn || expiresOn < todayStr) return false;

  return Number(lot.remainingQty) >= Number(fillQuantity);
};

/** The ids left out of the batch: they do not print, and so are never filled either. */
export const excludedLineItemIds = ({ rows, excludeBlocked }) =>
  new Set(
    excludeBlocked
      ? rows.filter((row) => row.blocksBatch).map((row) => row.item.requestLineItemId)
      : []
  );

/**
 * POM-6121 — may this lot be PICKED for the fill, given the operator can claim a shortfall?
 *
 * isLotUsable answers "does this lot cover the fill on its own", which is what the row's status
 * and the recorded-lot check still need. This answers the picker's question, which changed when
 * the shortfall claim arrived: a lot holding less than the fill is now offerable, because the
 * operator is shown what it holds and confirms the remainder is claimed as unverified.
 *
 * Deliberately a second predicate rather than a looser isLotUsable. That one decides NO_USABLE_LOT
 * and drives the "prints without a lot number" copy; widening it would quietly reclassify rows
 * that are meant to read as problems.
 *
 * `allowShortfall` off gives exactly isLotUsable, so the switch being off changes nothing.
 */
export const isLotSelectableForFill = ({ lot, fillQuantity, todayStr, allowShortfall }) => {
  if (!allowShortfall) return isLotUsable({ lot, fillQuantity, todayStr });
  if (!lot) return false;
  if (lot.existsInInventory === false) return false;

  const expiresOn = toDateOnly(lot.expirationDate);
  if (!expiresOn || expiresOn < todayStr) return false;

  return Number(lot.remainingQty) > 0;
};

/**
 * Every NDC's offerable lots, keyed by NDC.
 *
 * One map for everything the picker shows — the NDC cards, the in-stock count and the lot list all
 * read it, so a lot offerable in one place cannot be described as unavailable in another.
 */
export const selectableLotsByNdc = ({ groups, fillQuantity, todayStr, allowShortfall }) =>
  new Map(
    (groups ?? []).map((group) => [
      group.ndc,
      (group.lots ?? []).filter((lot) =>
        isLotSelectableForFill({ lot, fillQuantity, todayStr, allowShortfall })
      ),
    ])
  );

/**
 * How much of the fill this lot cannot cover. Zero unless it is short.
 *
 * No lot is zero, not the whole fill: every caller gates on `> 0` to mean "warn and ask", and
 * "nothing is selected yet" must not read as a shortfall the operator has to confirm.
 */
export const shortfallAgainstFill = ({ lot, fillQuantity }) =>
  lot ? Math.max(0, Number(fillQuantity || 0) - Number(lot.remainingQty || 0)) : 0;

/**
 * POM-6121 — may this lot be hand-recorded against?
 *
 * Deliberately a SEPARATE predicate rather than a loosening of `isLotUsable`. Every row status on
 * the screen is derived from that one, so relaxing it would quietly reclassify the whole grid: a
 * zero-quantity lot would start counting as a usable alternative, and the rows this feature exists
 * for would stop reporting that they have no stock.
 *
 * The rule is the inverse of usability on the quantity axis and silent on the others: a lot with
 * dispensable quantity must be picked from the normal picker (that path applies FEFO order and the
 * expiry check that hand-recording bypasses), while an expired or out-of-date lot IS recordable —
 * the operator has the vial in their hand, and refusing to record what was dispensed does not make
 * it undispensed.
 */
export const isRecordableLot = ({ lot }) => {
  if (!lot) return false;
  if (lot.existsInInventory === false) return false;

  return Number(lot.remainingQty ?? 0) <= 0;
};

/**
 * The NDC this row would print with right now — the pending edit if there is one, else what is
 * already on the record.
 */
export const effectiveNdcFor = ({ item, change }) => change?.ndc ?? item?.effectiveNdc ?? null;

/**
 * Is this row on an NDC other than the one its partner prefers?
 *
 * Deliberately NOT a ROW_STATUS. The statuses drive blocking and exclusion; this is advisory and
 * must never do either — a user can knowingly print off-preference, and the warning exists so the
 * choice is knowing rather than accidental.
 *
 * False whenever the partner has no preference for this product, which is the common case: core
 * returns null there, and a screen full of warnings nobody configured would train users to ignore
 * the ones that mean something. Also false for a row with no NDC at all — that row is already
 * blocked as NO_NDC, and "you picked the wrong NDC" is the wrong thing to tell someone who has
 * not picked one.
 *
 * Fires on untouched rows too, not only on edits: a row whose recorded NDC already differs is
 * off-preference whether or not this user is the one who put it there.
 */
export const isOffPreference = ({ item, change }) => {
  const preferred = item?.partnerPreferredNdc;
  if (!preferred) return false;

  const current = effectiveNdcFor({ item, change });

  return !!current && current !== preferred;
};

/** Why a recorded lot cannot be used — drives the badge text, not the blocking decision. */
/** Out of date as of today. A lot expiring today is still dispensable, matching the backend. */
const isExpiredOn = ({ lot, todayStr }) => {
  const expiresOn = toDateOnly(lot.expirationDate);

  return !expiresOn || expiresOn < todayStr;
};

/**
 * A BUD that has actually passed.
 *
 * isExpiredOn also refuses a lot carrying no BUD at all, which is right for a lot picked from
 * stock and wrong for a hand-recorded one: that lot is recorded before any receiving record
 * exists, and the panel states the BUD is optional because a barcode cannot carry it. Core draws
 * the same line — its manual branch deliberately bypasses the BUD-null rule — so the grid has to
 * draw it too, or it holds back a row core would fill.
 */
const hasPassedExpiry = ({ lot, todayStr }) => {
  const expiresOn = toDateOnly(lot.expirationDate);

  return !!expiresOn && expiresOn < todayStr;
};

const unusableReasonFor = ({ lot, fillQuantity, todayStr, shortfallAllowed }) => {
  if (lot.existsInInventory === false) return UNUSABLE_REASON.NOT_FOUND;

  if (isExpiredOn({ lot, todayStr })) return UNUSABLE_REASON.EXPIRED;

  // Gated, not arithmetic: this reason's whole copy tells the operator to pick the lot again and
  // claim the rest. With the shortfall path off the picker filters that lot out, so the sentence
  // would name a remedy the screen cannot offer.
  if (
    shortfallAllowed &&
    Number(lot.remainingQty) > 0 &&
    Number(lot.remainingQty) < Number(fillQuantity)
  ) {
    return UNUSABLE_REASON.SHORT_FOR_FILL;
  }

  return UNUSABLE_REASON.OUT_OF_STOCK;
};

/**
 * POM-6121 — the reason line, plus the way out when hand-recording is the only one.
 *
 * Appended rather than written into each gate so the five statuses that gained a pencil cannot
 * drift on how they describe it. Silent when the panel is unreachable: naming an option the row
 * does not have is the confusion the reason column exists to remove.
 */
const reasonWithManualOption = ({ base, canRecordManually }) =>
  canRecordManually ? `${base}, or record the lot you have by hand` : base;

/**
 * POM-6119 — why the reserved lot cannot be used as it stands, and what to do about it.
 *
 * A short lot gets its own sentence because its remedy is the opposite of the other two: the lot
 * in the picker is the right one and picking it again is the fix, where an expired or vanished lot
 * has to be replaced. Saying "no longer available" of a lot holding 10 of 25 sent the operator
 * looking for stock that does not exist.
 */
const unusableLotReason = ({ unusableReason, lot, fillQuantity }) => {
  if (unusableReason === UNUSABLE_REASON.EXPIRED) {
    return 'Reserved lot expired — pick another lot to print this item';
  }

  if (unusableReason === UNUSABLE_REASON.SHORT_FOR_FILL) {
    const shortfall = shortfallAgainstFill({ lot, fillQuantity });

    return (
      `Lot ${lot.lotNumber} holds ${formatQty(lot.remainingQty)} of ${formatQty(fillQuantity)} — ` +
      `pick it to dispense ${formatQty(lot.remainingQty)} and claim ${formatQty(
        shortfall
      )} as ${SYSTEM_DEFICIT.toLowerCase()}`
    );
  }

  return 'Reserved lot is no longer available — pick another lot to print this item';
};

/**
 * POM-6121 — the reason for a row that would print with NO lot at all.
 *
 * Such a row is no longer filled quietly and reconciled at the next count: a fill needs a lot, on
 * this screen as on the single-item panel. So the line names the consequence, and where there is
 * one, the way out. Left to the fill's failure report instead, the operator learns it after
 * committing a batch, with no idea which action would have avoided it.
 *
 * Separate from reasonWithManualOption because a row that HAS a usable lot borrows that suffix
 * too, and telling it that it will not be filled would be false.
 */
/**
 * What a row with no usable lot DOES, which the switch decides.
 *
 * On, it is left out of the batch rather than printed bare — so promising a print here would be
 * the opposite of what happens.
 */
const lotlessBase = ({ blockUnusableLotItems, what, missing = 'a lot number', unless = '' }) =>
  blockUnusableLotItems
    ? `${what} — left out of this batch${unless}`
    : `${what} — prints without ${missing}${unless}`;

const reasonForLotlessRow = ({
  base,
  canRecordManually,
  hasInventoryMaster,
  blockUnusableLotItems,
}) => {
  // Off, the row does print without a lot and does fill — the old copy is the true one.
  if (!blockUnusableLotItems) return reasonWithManualOption({ base, canRecordManually });

  // On, it prints nothing: it is left out of the batch, which is also what keeps it out of the
  // fill. So the line says that, and then the one action that changes it.
  if (canRecordManually) {
    return `${base}, or record the lot you have by hand`;
  }

  // No inventory master means no NDC to record a lot against, so nothing on this screen clears
  // it. The action belongs to an inventory user, and naming it beats a bare refusal.
  if (!hasInventoryMaster) {
    return `${base}. Ask an inventory user to add this product before it can be printed`;
  }

  return `${base}. No lot can be recorded for it here`;
};

const decorate = ({
  status,
  canEdit,
  blocksBatch,
  printsWithoutLot,
  reason,
  unusableReason,
  // POM-6119 — WHICH recorded lot `unusableReason` describes. On a multi-lot fill the first
  // unusable lot is not recordedLots[0], so the grid read one lot's numbers under the other
  // lot's verdict. Carried rather than re-derived so the two cannot name different lots.
  unusableLot,
  // POM-6121 — this row's staged edit is a hand-recorded claim, not an allocation from stock.
  // Carried on the row rather than re-derived in the grid so the badge, the reason line and any
  // future count all read the one answer.
  recordsUnverifiedLot,
}) => ({
  status,
  canEdit,
  blocksBatch,
  printsWithoutLot: !!printsWithoutLot,
  reason,
  unusableReason,
  unusableLot: unusableLot ?? null,
  recordsUnverifiedLot: !!recordsUnverifiedLot,
});

/**
 * Classify one row.
 *
 * Gate order is load-bearing and mirrors the commit validator's: identity and hard state first
 * (dispensed, locked, no catalogue), then NDC, then lots. Telling a user to pick a new lot for a
 * row whose stock has already moved sends them after the wrong problem.
 */
export const classifyRow = ({
  item,
  availability,
  todayStr,
  change,
  recordable,
  isManualLotEnabled,
  blockUnusableLotItems,
}) => {
  const fillQuantity = Number(item.fillQuantity ?? 0);
  const groups = availability?.groups ?? [];

  /**
   * POM-6121 — can this row reach the manual panel at all?
   *
   * Four things have to hold, and all four are facts rather than judgements: the portal switch is
   * on, core says the fill may carry a hand-recorded claim, inventory holds at least one NDC for
   * the product to record against, and the caller sent a recordable payload for this product and
   * facility. With no inventory master there is nothing valid to enter, which is why that row
   * keeps its read-only status instead of being handed a panel that can only refuse.
   *
   * The switch arrives as a parameter rather than being read here, so this module stays pure — and
   * it has to be asked at all because it is NOT the switch core answers on. The portal env flag
   * rolls out on its own schedule while canRecordUnverifiedLot is gated server-side on AppConfig,
   * so server-on plus portal-off is a real state — and it is the state the flag exists for.
   * Ignoring it gave those rows a pencil and a reason line offering to record by hand, and the
   * pencil then opened a dialog with no manual button and an empty lot list.
   */
  // Both switches, not either — see FillProductLotDialog: blocking without the remedy available
  // leaves the operator nowhere to go, so the whole rule follows manual entry being on.
  const blocksWithoutUsableLot = isManualLotEnabled === true && blockUnusableLotItems === true;

  const canRecordManually =
    isManualLotEnabled === true &&
    item.canRecordUnverifiedLot === true &&
    !!recordable?.hasInventoryMaster &&
    (recordable?.ndcOptions ?? []).length > 0;

  /**
   * POM-6119 — the picker's own rule, asked here too.
   *
   * The shortfall split (POM-6121) taught the picker that a lot holding part of the fill is
   * offerable, but the grid kept asking the strict question, so a row whose only lot held 10 of 25
   * classified as having nothing usable — and once that classification also excluded the row from
   * the batch, the operator was told to fix a row the screen called unfixable. The two questions
   * are different and both are needed, so both are asked, by name.
   */
  const shortfallAllowed = isManualLotEnabled === true && item.canRecordUnverifiedLot === true;

  // Lots that cover the whole quantity by themselves — nothing to confirm, nothing claimed.
  const lotsCoveringFill = groups.flatMap((group) =>
    (group.lots ?? []).filter((lot) => isLotUsable({ lot, fillQuantity, todayStr }))
  );
  const hasLotCoveringFill = lotsCoveringFill.length > 0;

  // A lot the picker would put in front of the operator, covering or short.
  const hasOfferableLot = groups.some((group) =>
    (group.lots ?? []).some((lot) =>
      isLotSelectableForFill({ lot, fillQuantity, todayStr, allowShortfall: shortfallAllowed })
    )
  );

  // An uncommitted edit is the newest fact about the row, and by construction it names a lot that
  // was usable when picked — so it supersedes whatever the underlying state said, including a block.
  if (change) {
    const recordsUnverifiedLot = hasManualSelection(change);

    return decorate({
      status: ROW_STATUS.CHANGED,
      canEdit: true,
      blocksBatch: false,
      recordsUnverifiedLot,
      // A hand-recorded row is still CHANGED — it commits and reprints like any other edit — but
      // what it commits is a claim against stock inventory never received, so the row says so
      // rather than reading as an ordinary lot swap.
      reason: recordsUnverifiedLot
        ? `Recorded by hand — commits as ${SYSTEM_DEFICIT.toLowerCase()} quantity, then the label rebuilds`
        : 'Changed — commit to regenerate the label',
    });
  }

  if (item.isDispensed) {
    return decorate({
      status: ROW_STATUS.ALREADY_DISPENSED,
      canEdit: false,
      blocksBatch: false,
      reason: 'Already dispensed — change it from Fill Product',
    });
  }

  if (availability?.isLockedForPhysicalCount) {
    return decorate({
      status: ROW_STATUS.LOCKED_FOR_COUNT,
      canEdit: false,
      blocksBatch: false,
      reason: 'Locked for physical count',
    });
  }

  /**
   * POM-6119 — a decision the operator already committed outranks every question below.
   *
   * Above the catalogue gate on purpose. A hand-recorded lot is created at remaining_qty 0, and
   * listAvailableLotsForDispense filters that out — so a product whose only lot is the one just
   * recorded has NO availability at all, and the row returned at that gate as "no inventory for
   * this product" while carrying the very lot the operator had entered. The catalogue describes
   * what is dispensable NOW; it cannot answer what was already agreed.
   */
  const recordedLots = item.recordedLots ?? [];

  const unusable = recordedLots.find((lot) => !isLotUsable({ lot, fillQuantity, todayStr }));

  // Both of these sit ahead of the unusable branch, which would find the same lot again. A commit
  // moves no stock, so neither quantity can change before the fill runs: asking again blocks the
  // row forever. Expiry is still checked — permission covers a missing receiving record, not a
  // lot that has gone out of date.
  //
  // A hand-recorded lot is created at remaining_qty 0 BY DESIGN: there is no receiving record yet,
  // which is the whole reason it was typed. Read as a quantity it says "nothing usable", and the
  // row was held back telling the operator to record a lot by hand — which they had just done.
  const handRecordedLot = recordedLots.find(
    (lot) =>
      lot.isManualEntry === true &&
      lot.existsInInventory !== false &&
      !hasPassedExpiry({ lot, todayStr })
  );

  const committedShortfallLot = recordedLots.find(
    (lot) =>
      lot.allowShortfallAsUnverified === true &&
      lot.existsInInventory !== false &&
      Number(lot.remainingQty) > 0 &&
      !isExpiredOn({ lot, todayStr })
  );

  /**
   * Two bounds on the settlement, both load-bearing.
   *
   * It excuses the lot it was given for and nothing else: a multi-lot row whose OTHER lot is
   * expired or gone must still block, or the label names a lot that cannot be dispensed. Nothing
   * enforces one lot per commit — `lotAllocations` is a list all the way down to drug_dispensed.
   *
   * And it never outranks a missing NDC. The commit writes prescription_fill.ndc, so a committed
   * row should always have one; asserted rather than assumed, because every defect in this chain
   * has come from trusting exactly that kind of invariant.
   */
  const settledLot = item.effectiveNdc ? (committedShortfallLot ?? handRecordedLot) : null;
  const onlyTheCommittedLotStandsOut = !unusable || unusable === settledLot;
  const settled = shortfallAllowed && !!settledLot && onlyTheCommittedLotStandsOut;

  if (settled && settledLot === handRecordedLot) {
    return decorate({
      status: ROW_STATUS.MANUAL_LOT_COMMITTED,
      // Not a literal: this gate runs above the catalogue check, so a settled row can reach it
      // with nothing to offer. A pencil opening an empty dialog is the dead end POM-6121 fixed.
      canEdit: canRecordManually || hasOfferableLot,
      blocksBatch: false,
      unusableLot: handRecordedLot,
      reason: `Lot ${handRecordedLot.lotNumber} recorded by hand — ${formatQty(
        fillQuantity
      )} will be claimed as ${SYSTEM_DEFICIT.toLowerCase()} when it fills`,
    });
  }

  if (settled) {
    const shortfall = shortfallAgainstFill({ lot: committedShortfallLot, fillQuantity });

    return decorate({
      status: ROW_STATUS.SHORTFALL_COMMITTED,
      canEdit: canRecordManually || hasOfferableLot,
      blocksBatch: false,
      unusableLot: committedShortfallLot,
      reason:
        shortfall > 0
          ? `Lot ${committedShortfallLot.lotNumber} holds ${formatQty(
              committedShortfallLot.remainingQty
            )} of ${formatQty(fillQuantity)} — ${formatQty(shortfall)} will be claimed as ${SYSTEM_DEFICIT.toLowerCase()} when it fills`
          : `Confirmed to claim any shortfall as ${SYSTEM_DEFICIT.toLowerCase()} when it fills`,
    });
  }

  if (!availability || groups.length === 0) {
    return decorate({
      // POM-6121: a product can have inventory-master NDCs and no stock anywhere, which is
      // exactly the row an operator holding the vial needs the manual panel for. The status stays
      // NO_CATALOG — nothing here can be dispensed — but it is no longer read-only when a claim
      // can be recorded.
      status: ROW_STATUS.NO_CATALOG,
      canEdit: canRecordManually,
      // POM-6121 — a label needs a lot that can actually be dispensed, so a row that would print
      // without one blocks the batch like an expired lot does: it is left out by default, which
      // is also what keeps it out of the fill (an id left out never reaches markLineItemsPrinted).
      blocksBatch: blocksWithoutUsableLot,
      printsWithoutLot: !blocksWithoutUsableLot,
      // This gate deliberately runs BEFORE the NDC check, because a row with no catalogue cannot
      // be given an NDC from this screen whether it has one or not. But the badge then reports
      // only the catalogue, which left rows showing a blank NDC column under a bare "No inventory"
      // and no mention of the missing NDC anywhere. Say both.
      reason: reasonForLotlessRow({
        base: item.effectiveNdc
          ? lotlessBase({
              blockUnusableLotItems: blocksWithoutUsableLot,
              what: 'No inventory for this product at this facility',
            })
          : lotlessBase({
              blockUnusableLotItems: blocksWithoutUsableLot,
              what: 'No NDC assigned, and no inventory for this product here',
              missing: 'an NDC or lot number',
            }),
        canRecordManually,
        hasInventoryMaster: !!recordable?.hasInventoryMaster,
        blockUnusableLotItems: blocksWithoutUsableLot,
      }),
    });
  }

  if (!item.effectiveNdc) {
    // Fixable from this screen, which is exactly why it blocks rather than printing bare.
    return decorate({
      status: ROW_STATUS.NO_NDC,
      canEdit: hasOfferableLot || canRecordManually,
      blocksBatch: true,
      reason: hasOfferableLot
        ? 'No NDC assigned — pick an NDC and lot'
        : reasonForLotlessRow({
            base: 'No NDC assigned, and no lot in stock covers this quantity',
            canRecordManually,
            hasInventoryMaster: !!recordable?.hasInventoryMaster,
            blockUnusableLotItems: blocksWithoutUsableLot,
          }),
    });
  }

  if (recordedLots.length === 0) {
    return decorate({
      status: ROW_STATUS.NO_LOT,
      canEdit: hasOfferableLot || canRecordManually,
      // POM-6121 — a label needs a lot that can actually be dispensed, so a row that would print
      // without one blocks the batch like an expired lot does: it is left out by default, which
      // is also what keeps it out of the fill (an id left out never reaches markLineItemsPrinted).
      blocksBatch: blocksWithoutUsableLot,
      printsWithoutLot: !blocksWithoutUsableLot,
      reason: hasOfferableLot
        ? lotlessBase({
            blockUnusableLotItems: blocksWithoutUsableLot,
            what: 'No lot recorded',
            unless: ' unless you pick one',
          })
        : reasonForLotlessRow({
            base: lotlessBase({
              blockUnusableLotItems: blocksWithoutUsableLot,
              what: 'No lot in stock',
            }),
            canRecordManually,
            hasInventoryMaster: !!recordable?.hasInventoryMaster,
            blockUnusableLotItems: blocksWithoutUsableLot,
          }),
    });
  }

  if (unusable) {
    const unusableReason = unusableReasonFor({
      lot: unusable,
      fillQuantity,
      todayStr,
      shortfallAllowed,
    });

    // The split rule: a fixable problem blocks, an unfixable one prints without a lot rather than
    // deadlocking the batch — and the unusable lot number is never printed either way.
    if (!hasOfferableLot) {
      return decorate({
        // POM-6121: the row this feature was raised for. Still non-blocking and still prints
        // without a lot if nothing is done, but the operator holding the product now has a way
        // through instead of a read-only row telling them there is none.
        status: ROW_STATUS.NO_USABLE_LOT,
        canEdit: canRecordManually,
        // POM-6121 — see the NO_INVENTORY gate: no usable lot blocks the batch under the switch.
        blocksBatch: blocksWithoutUsableLot,
        printsWithoutLot: !blocksWithoutUsableLot,
        unusableReason,
        unusableLot: unusable,
        reason: reasonForLotlessRow({
          base: lotlessBase({
            blockUnusableLotItems: blocksWithoutUsableLot,
            what: 'No usable lot in stock',
          }),
          canRecordManually,
          hasInventoryMaster: !!recordable?.hasInventoryMaster,
          blockUnusableLotItems: blocksWithoutUsableLot,
        }),
      });
    }

    return decorate({
      status: ROW_STATUS.UNUSABLE_LOT,
      canEdit: true,
      blocksBatch: true,
      unusableReason,
      unusableLot: unusable,
      reason: unusableLotReason({ unusableReason, lot: unusable, fillQuantity }),
    });
  }

  // Nothing to decide: one NDC, one usable lot, and it is the one already recorded.
  const onlyOneChoice =
    groups.length === 1 &&
    lotsCoveringFill.length === 1 &&
    recordedLots.length === 1 &&
    lotsCoveringFill[0].inventoryLotId === recordedLots[0].inventoryLotId;

  if (onlyOneChoice) {
    return decorate({
      status: ROW_STATUS.NO_ALTERNATIVE,
      canEdit: false,
      blocksBatch: false,
      reason: 'Only one NDC and one lot — nothing to change',
    });
  }

  return decorate({
    status: ROW_STATUS.AS_ALLOCATED,
    // A multi-lot row no single lot can cover has nothing to offer the v1 editor from stock —
    // POM-6121 still lets it be recorded by hand, which is what the operator needs when the lot in
    // the picker is not the lot in their hand.
    canEdit: hasLotCoveringFill || canRecordManually,
    blocksBatch: false,
    // Told apart by whether the row can actually be edited. Offering "change it" on a row with no
    // pencil is the confusion this column exists to remove — and it repeated the chip's own word
    // besides, so the line said nothing at all.
    reason: hasLotCoveringFill
      ? 'Reserved lot is in date and covers this quantity — change it only if you need to'
      : reasonWithManualOption({
          base: 'Reserved lot is in date — no other single lot here covers this quantity',
          canRecordManually,
        }),
  });
};

/**
 * Classify a whole selection and derive the counts the dialog's banner and bottom bar display.
 *
 * OTC rows and rows with no productId are filtered out BEFORE classification: neither can ever
 * carry a lot (resolveLabelLotNumber returns nothing for OTC, and the catalogue is keyed on
 * productId), so there is no decision to offer. They still count toward `total`, because the
 * batch total must always reconcile with what the user selected.
 *
 * `selectedCount` is what makes that reconciliation true, and it has to be passed in: the review
 * query can return FEWER items than were selected — a line item that no longer exists, or one
 * outside the caller's permitted fulfilling pharmacies, is dropped server-side. Deriving the total
 * from `items` therefore reported the batch as smaller than it is ("15 of the 15 selected") while
 * the PDF and mark-as-printed still ran on every id the user picked. The missing rows print
 * unchanged, which is the pre-review behaviour and deliberately kept — but they must be counted
 * and named, not quietly absorbed.
 */
export const classifyRows = ({
  items,
  availability,
  recordableLots,
  todayStr,
  changes,
  selectedCount,
  isManualLotEnabled,
  blockUnusableLotItems,
}) => {
  const availabilityByKey = new Map(
    (availability ?? []).map((entry) => [
      `${entry.productId}::${entry.fulfillingPharmacyId}`,
      entry,
    ])
  );
  // POM-6121: keyed exactly as availability is, because the answer is per product AND facility.
  const recordableByKey = new Map(
    (recordableLots ?? []).map((entry) => [
      `${entry.productId}::${entry.fulfillingPharmacyId}`,
      entry,
    ])
  );

  const reviewable = (items ?? []).filter((item) => !item.isOtc && !!item.productId);

  const classified = reviewable.map((item) => ({
    item,
    ...classifyRow({
      item,
      availability: availabilityByKey.get(`${item.productId}::${item.fulfillingPharmacyId}`),
      recordable: recordableByKey.get(`${item.productId}::${item.fulfillingPharmacyId}`),
      todayStr,
      change: changes?.[item.requestLineItemId],
      isManualLotEnabled,
      blockUnusableLotItems,
    }),
  }));

  const rows = classified
    .filter((row) => !HIDDEN_STATUSES.has(row.status))
    .sort((a, b) => statusSortRank(a.status) - statusSortRank(b.status));

  // What came back from the review, and what the user actually selected.
  const reviewed = (items ?? []).length;
  const total = selectedCount ?? reviewed;
  const blocked = classified.filter((row) => row.blocksBatch).length;

  return {
    rows,
    counts: {
      total,
      reviewed,
      // Selected but absent from the review payload. Never negative: a payload larger than the
      // selection would mean a caller mismatch, and reporting a negative count would be worse
      // than reporting none.
      unreviewed: Math.max(0, total - reviewed),
      // Derived from `reviewed`, NOT `total`: an item the review never saw has not been checked
      // and found to need nothing, so counting it as auto-included would claim a guarantee that
      // was never made.
      autoIncluded: reviewed - rows.length,
      listed: rows.length,
      // Listed and actually waiting on someone. Being listed is not the same as needing a
      // decision: a read-only row is here to be read, and a settled one has already been decided.
      // Calling either a decision sends the user hunting for work that is not there.
      actionable: rows.filter((row) => row.canEdit && !SETTLED_STATUSES.has(row.status)).length,
      informational: rows.filter((row) => !row.canEdit || SETTLED_STATUSES.has(row.status)).length,
      // Only the non-blocking ones. A blocked row does print now, but it prints WITH something
      // wrong — an expired lot, or no NDC — so counting it here would tell the user the wrong
      // thing about it.
      withoutLot: rows.filter((row) => row.printsWithoutLot && !row.blocksBatch).length,
      blocked,
      changed: classified.filter((row) => row.status === ROW_STATUS.CHANGED).length,
      printable: total - blocked,
    },
  };
};
